import { compact } from 'lodash';
import { useCallback, useMemo } from 'react';

import {
  ButtonWithPopover,
  ButtonWithPopoverProps,
} from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useModalState } from '@/hooks/useModalState';
import { AIOnboardingModal } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal';
import { AIOnboardingModalVariant } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { GenerateFromDocumentsModal } from '@/modules/aiOnboarding/components/GenerateFromDocumentsModal';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { ClientOnboardingDeliverableSelectionModal } from '@/modules/clientOnboarding/ClientOnboardingDeliverableModal/ClientOnboardingDeliverableSelectionModal';
import { ClientOnboardingDeliverableVariant } from '@/modules/clientOnboarding/types';
import { AddNewAnythingModal } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import {
  BUSINESS_ENTITY_TYPES,
  CHARITABLE_ENTITY_TYPES,
  GRANTOR_ENTITY_TYPES,
} from '@/modules/entities/entities.constants';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { DefaultEstateTaxStatus } from '@/modules/entities/testamentaryEntities/shared/testamentaryEntity.types';
import { TestamentaryEntityModal } from '@/modules/entities/testamentaryEntities/TestamentaryEntityModal';
import { AddHoldingsFormModal } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal';
import { EstateHoldingsProvider } from '@/modules/estate/contexts/EstateHoldings.provider';
import { CreateEstateWaterfallTrowser } from '@/modules/estateWaterfall/components/EstateWaterfallModal/CreateEstateWaterfallTrowser';
import { useEstateWaterfallMenuItems } from '@/modules/estateWaterfall/components/EstateWaterfallsDropdown/hooks/useEstateWaterfallMenuItems';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { ROUTE_KEYS } from '@/navigation/constants';
import { AiSuggestionKind } from '@/types/schema';

type CreateNewKind =
  | 'testamentary_entity'
  | 'individual_or_organization'
  | 'waterfall'
  | 'strategy'
  | 'add_holdings'
  | 'create_trust'
  | 'create_business';

export type CreateNewEntityOrStrategyVariant =
  | 'default'
  | 'entity_only'
  | 'data_only';

const DEFAULT_BLANK_TAX_STATUS: DefaultEstateTaxStatus = {};

interface CreateNewEntityOrStrategyProps
  extends Omit<Partial<ButtonWithPopoverProps>, 'variant'> {
  householdId: string;
  variant?: CreateNewEntityOrStrategyVariant;
  onAfterCreateIndividualOrOrganization?: () => void;
}

export function CreateNewEntityOrStrategy({
  householdId,
  variant = 'default',
  onAfterCreateIndividualOrOrganization,
  ...buttonProps
}: CreateNewEntityOrStrategyProps) {
  const canEdit = useCanEditHouseholdData();

  const { projectionFunctionalityEnabled } = useTenantDetailsContext();
  const canCreateEstateWaterfalls = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_CREATE_UPDATE_DELETE_ESTATE_WATERFALLS
  );

  const { navigate } = useNavigateToRoute();

  const { primaryClients: grantors, isTwoClientHousehold } =
    useHouseholdDetailsContext();

  const { clientWaterfalls } = useEstateWaterfallMenuItems({
    householdId,
    waterfallId: null,
  });

  const [
    { isModalOpen: isGenerateFromDocumentsModalOpen },
    { setModalIsOpen: setIsGenerateFromDocumentsModalOpen },
  ] = useModalState();
  const [
    { isModalOpen: isAIOnboardingModalOpen },
    { setModalIsOpen: setIsAIOnboardingModalOpen },
  ] = useModalState();
  const [
    { isModalOpen: isCreateTestamentaryEntityModalOpen },
    { setModalIsOpen: setIsCreateTestamentaryEntityModalOpen },
  ] = useModalState();
  const [
    { isModalOpen: isAddNewIndividualOrOrganizationModalOpen },
    { setModalIsOpen: setIsAddNewIndividualOrOrganizationModalOpen },
  ] = useModalState();
  const [
    { isModalOpen: isCreateWaterfallModalOpen },
    { setModalIsOpen: setIsCreateWaterfallModalOpen },
  ] = useModalState();
  const [
    { isModalOpen: isModelANewStrategyOpen },
    { setModalIsOpen: setIsModelANewStrategyOpen },
  ] = useModalState();
  const [
    { isModalOpen: isAddHoldingsModalOpen },
    { setModalIsOpen: setIsAddHoldingsModalOpen },
  ] = useModalState();
  const [
    { isModalOpen: isCreateTrustModalOpen },
    { setModalIsOpen: setIsCreateTrustModalOpen },
  ] = useModalState();
  const [
    { isModalOpen: isCreateBusinessModalOpen },
    { setModalIsOpen: setIsCreateBusinessModalOpen },
  ] = useModalState();

  const handleClose = useCallback(() => {
    setIsGenerateFromDocumentsModalOpen(false);
    setIsAIOnboardingModalOpen(false);
    setIsCreateTestamentaryEntityModalOpen(false);
    setIsAddNewIndividualOrOrganizationModalOpen(false);
    setIsCreateWaterfallModalOpen(false);
    setIsModelANewStrategyOpen(false);
    setIsAddHoldingsModalOpen(false);
    setIsCreateTrustModalOpen(false);
    setIsCreateBusinessModalOpen(false);
  }, [
    setIsGenerateFromDocumentsModalOpen,
    setIsAIOnboardingModalOpen,
    setIsCreateTestamentaryEntityModalOpen,
    setIsAddNewIndividualOrOrganizationModalOpen,
    setIsCreateWaterfallModalOpen,
    setIsModelANewStrategyOpen,
    setIsAddHoldingsModalOpen,
    setIsCreateTrustModalOpen,
    setIsCreateBusinessModalOpen,
  ]);

  const handleClick = useCallback(
    (kind: CreateNewKind) => {
      if (kind === 'testamentary_entity') {
        setIsCreateTestamentaryEntityModalOpen(true);
      }
      if (kind === 'individual_or_organization') {
        setIsAddNewIndividualOrOrganizationModalOpen(true);
      }
      if (kind === 'waterfall') {
        setIsCreateWaterfallModalOpen(true);
      }
      if (kind === 'strategy') {
        setIsModelANewStrategyOpen(true);
      }
      if (kind === 'add_holdings') {
        setIsAddHoldingsModalOpen(true);
      }
      if (kind === 'create_trust') {
        setIsCreateTrustModalOpen(true);
      }
      if (kind === 'create_business') {
        setIsCreateBusinessModalOpen(true);
      }
    },
    [
      setIsCreateTestamentaryEntityModalOpen,
      setIsAddNewIndividualOrOrganizationModalOpen,
      setIsCreateWaterfallModalOpen,
      setIsModelANewStrategyOpen,
      setIsAddHoldingsModalOpen,
      setIsCreateTrustModalOpen,
      setIsCreateBusinessModalOpen,
    ]
  );

  const menuItems = useMemo(() => {
    const defaultVariantMenuItems =
      variant === 'default'
        ? [
            <MenuItem
              key="newIndividualOrOrganization"
              label="New individual or organization"
              onClick={() => handleClick('individual_or_organization')}
            />,
            <MenuItem
              key="addHoldings"
              label="Add holdings"
              onClick={() => handleClick('add_holdings')}
            />,
            <MenuItem
              key="createTrust"
              label="Create trust"
              onClick={() => handleClick('create_trust')}
            />,
            <MenuItem
              key="createBusiness"
              label="Create business"
              onClick={() => handleClick('create_business')}
            />,
            projectionFunctionalityEnabled && canCreateEstateWaterfalls && (
              <MenuItem
                key="newWaterfall"
                label="New waterfall"
                onClick={() => handleClick('waterfall')}
              />
            ),
            <MenuItem
              key="newAnalysis"
              label="New analysis"
              onClick={() => handleClick('strategy')}
            />,
          ]
        : [];

    const dataOnlyVariantMenuItems =
      variant === 'data_only'
        ? [
            <MenuItem
              key="addHoldings"
              label="Add holdings"
              onClick={() => handleClick('add_holdings')}
            />,
            <MenuItem
              key="createTrust"
              label="Create trust"
              onClick={() => handleClick('create_trust')}
            />,
            <MenuItem
              key="createBusiness"
              label="Create business"
              onClick={() => handleClick('create_business')}
            />,
          ]
        : [];

    return compact([
      ...defaultVariantMenuItems,
      ...dataOnlyVariantMenuItems,
      variant === 'entity_only' && (
        <MenuItem
          key="newTestamentaryEntity"
          label="New sub-trust"
          onClick={() => handleClick('testamentary_entity')}
        />
      ),
    ]);
  }, [
    variant,
    projectionFunctionalityEnabled,
    canCreateEstateWaterfalls,
    handleClick,
  ]);

  if (!canEdit) {
    return null;
  }

  return (
    <>
      {isGenerateFromDocumentsModalOpen && (
        <GenerateFromDocumentsModal
          isOpen={isGenerateFromDocumentsModalOpen}
          onClose={() => setIsGenerateFromDocumentsModalOpen(false)}
        />
      )}
      {isAIOnboardingModalOpen && (
        <AIOnboardingModal
          isOpen={isAIOnboardingModalOpen}
          onClose={() => setIsAIOnboardingModalOpen(false)}
          variant={AIOnboardingModalVariant.ALL_ENTITIES}
          householdId={householdId}
          suggestionsWhere={{
            kindIn: [
              AiSuggestionKind.OnboardingEntity,
              AiSuggestionKind.OnboardingTestamentaryEntity,
            ],
            acceptanceStatusIsNil: true,
          }}
        />
      )}
      {isCreateTestamentaryEntityModalOpen && (
        <TestamentaryEntityModal
          isOpen={isCreateTestamentaryEntityModalOpen}
          onClose={() => setIsCreateTestamentaryEntityModalOpen(false)}
          dyingPrimaryClientId={null}
          deadClientIds={[]}
          isTwoClientHousehold={isTwoClientHousehold}
          defaultEstateTaxStatus={DEFAULT_BLANK_TAX_STATUS}
        />
      )}
      <AddNewAnythingModal
        handleClose={() => setIsAddNewIndividualOrOrganizationModalOpen(false)}
        isOpen={isAddNewIndividualOrOrganizationModalOpen}
        householdId={householdId}
        addableTypes={['individual', 'organization']}
        onAfterCreate={() => {
          setIsAddNewIndividualOrOrganizationModalOpen(false);
          onAfterCreateIndividualOrOrganization?.();
        }}
      />

      {isCreateWaterfallModalOpen && (
        <CreateEstateWaterfallTrowser
          isOpen={isCreateWaterfallModalOpen}
          onClose={() => setIsCreateWaterfallModalOpen(false)}
          householdId={householdId}
          grantors={grantors ?? []}
          sourceEstateWaterfalls={clientWaterfalls}
        />
      )}
      <ClientOnboardingDeliverableSelectionModal
        isOpen={isModelANewStrategyOpen}
        onClose={handleClose}
        initialVariant={ClientOnboardingDeliverableVariant.CREATE_ANALYSIS}
      />
      {isAddHoldingsModalOpen && (
        <EstateHoldingsProvider>
          <AddHoldingsFormModal
            open={isAddHoldingsModalOpen}
            onClose={() => setIsAddHoldingsModalOpen(false)}
            holdingsGroup={undefined}
          />
        </EstateHoldingsProvider>
      )}
      <CreateEntityShortFormModal
        isOpen={isCreateTrustModalOpen}
        onClose={() => setIsCreateTrustModalOpen(false)}
        householdId={householdId}
        limitEntityTypes={[...GRANTOR_ENTITY_TYPES, ...CHARITABLE_ENTITY_TYPES]}
        heading="Create trust"
        entityTypeLabel="Trust type"
        buttonText="Create trust"
        onAfterCreate={() => {
          navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS, { householdId });
        }}
      />
      <CreateEntityShortFormModal
        isOpen={isCreateBusinessModalOpen}
        onClose={() => setIsCreateBusinessModalOpen(false)}
        householdId={householdId}
        limitEntityTypes={[...BUSINESS_ENTITY_TYPES]}
        heading="Create business"
        entityTypeLabel="Business type"
        buttonText="Create business"
        onAfterCreate={() => {
          navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES, { householdId });
        }}
      />
      <ButtonWithPopover
        label="Create"
        startIcon={PlusIcon}
        variant="primary"
        size="sm"
        popperVariant="menuBelow"
        {...buttonProps}
      >
        {menuItems}
      </ButtonWithPopover>
    </>
  );
}
